/** /api_credentials/ — API_Credential management across tenants.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). The `filters` param is the QuerySearch widget's wire
 * AST, URL-encoded as a JSON array — identical contract to
 * /notification_subscriptions/, but over API_Credential (see
 * ApiCredentialFilterNode in the OpenAPI spec). Small table — no mandatory
 * time bound; rows have integer ids. Lists root credentials only; jwt_refresh
 * children ride along in the detail payload.
 *
 * Secrets: modern keys are stored hashed — the raw key exists ONLY in the 201
 * create response (`raw_key`) and can never be retrieved again. `legacy_key`
 * (plaintext legacy rows) is returned only by `retrieve(id, {reveal: true})`,
 * which requires `console_api_credentials_reveal` and is audited server-side
 * per actual disclosure.
 */
import { apiDelete, apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export type ApiCredentialStatus = 'active' | 'disabled' | 'revoked';
/** `partner_dev` = an integration partner's developer key on its sandbox — minted only by
 *  the partner's claim link, never by `create`. */
export type ApiCredentialScope = 'client' | 'admin' | 'tour_operator' | 'webhooks' | 'partner_dev';
/** The scopes `create` accepts (`partner_dev` is claim-link only). */
export type ApiCredentialCreateScope = Exclude<ApiCredentialScope, 'partner_dev'>;
export type ApiCredentialType = 'opaque' | 'jwt_refresh';

/** ConsoleTenantRef — the tenant reference every console list serialises. */
export interface ApiCredentialTenantRef {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface ApiCredentialListItem {
  id: number;
  /** null = system/platform credential (no owning tenant). */
  tenant: ApiCredentialTenantRef | null;
  name: string;
  scope: ApiCredentialScope;
  /** The integration partner holding the key (`partner_dev` keys); null/absent otherwise. */
  partner?: { id: number; code: string; name: string } | null;
  status: ApiCredentialStatus;
  credential_type: ApiCredentialType;
  /** First 8 chars of the raw key, after its `tosbx_` marker if any (display only; empty
   *  for legacy-only rows). */
  key_prefix: string;
  /** HMAC fingerprint — the safe-for-logging identifier. */
  key_fingerprint: string;
  /** true when a plaintext legacy key is stored (revealable, permission-gated). */
  has_legacy_key: boolean;
  expires: string | null;
  created_ts: string | null;
  updated_ts: string | null;
  last_used: string | null;
}

export interface ApiCredentialUserRef {
  id: number;
  email: string;
}

/** A jwt_refresh child credential (deleted by CASCADE with its parent). */
export interface ApiCredentialChild {
  id: number;
  status: ApiCredentialStatus;
  credential_type: ApiCredentialType;
  key_prefix: string;
  key_fingerprint: string;
  created_ts: string | null;
  expires: string | null;
  last_used: string | null;
}

export interface ApiCredentialDetail extends ApiCredentialListItem {
  description: string;
  user: ApiCredentialUserRef | null;
  created_by: ApiCredentialUserRef | null;
  last_used_ip: string | null;
  /** Set flags of the transitional Import_Access_Token access bitfield. */
  legacy_access: string[];
  /** RBAC access groups (read-only here; managed via the dashboard API). */
  access_groups: Array<{ id: number; name: string }>;
  parent_id: number | null;
  children: ApiCredentialChild[];
  /** Webhook subscriptions bound to this credential (blocks deletion). */
  notification_subscription_count: number;
  /** Extranet (Zapier) subscriptions bound to this credential (blocks deletion). */
  extranet_subscription_count: number;
  /** Legacy Import_Access_Token rows linking here (non-blocking). */
  import_token_count: number;
  /** Present only on `retrieve(id, {reveal: true})`; null for hash-only rows. */
  legacy_key?: string | null;
}

export interface ApiCredentialCreate {
  /** Owning tenant id, or null for a system/platform credential. */
  tour_operator: number | null;
  name: string;
  description?: string | null;
  scope: ApiCredentialCreateScope;
  expires?: string | null;
}

export interface ApiCredentialCreateResponse extends ApiCredentialDetail {
  /** The generated raw key — returned exactly once, in this response only. */
  raw_key: string;
}

/** Key-present semantics; any subset. Everything else is immutable via the
 *  console (400). `status: revoked` cascades to jwt_refresh children and is
 *  terminal. `expires: null` clears the expiration. */
export interface ApiCredentialUpdate {
  name?: string;
  description?: string | null;
  status?: ApiCredentialStatus;
  expires?: string | null;
}

export interface ApiCredentialListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  /** id|tenant|name|scope|status|type|created_ts|expires|last_used
   *  ('-' desc). Default -created_ts. */
  ordering?: string;
  /** Authoritative tenant scope, ANDed on top of `filters`. */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
}

function toQuery(p: ApiCredentialListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(
  p: ApiCredentialListParams = {},
): Promise<PaginatedResponse<ApiCredentialListItem>> {
  const qs = toQuery(p);
  return apiGet(`/api_credentials/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** `reveal: true` includes the raw legacy key (403 without the reveal
 *  permission; each actual disclosure is audited server-side). */
export async function retrieve(
  id: number | string,
  opts: { reveal?: boolean } = {},
): Promise<ApiCredentialDetail> {
  return apiGet(`/api_credentials/${id}/${opts.reveal ? '?reveal=true' : ''}`);
}

/** POST — mints an opaque key. The 201 body carries the one-time `raw_key`;
 *  keep it in component state only (never in a query cache). */
export async function create(body: ApiCredentialCreate): Promise<ApiCredentialCreateResponse> {
  return apiPost(`/api_credentials/`, body);
}

export async function update(
  id: number | string,
  body: ApiCredentialUpdate,
): Promise<ApiCredentialDetail> {
  return apiPatch(`/api_credentials/${id}/`, body);
}

/** DELETE — 409 while webhook/extranet subscriptions reference the
 *  credential (body carries the blocking counts). */
export async function remove(id: number | string): Promise<void> {
  return apiDelete(`/api_credentials/${id}/`);
}
